import classNames from 'classnames';
import {CSSProperties} from 'react';
import {chart} from '@xh/hoist/cmp/chart';
import {div, hframe, span, vbox} from '@xh/hoist/cmp/layout';
import {hoistCmp} from '@xh/hoist/core';
import {toolbarSep} from '@xh/hoist/desktop/cmp/toolbar';
import {tooltip} from '@xh/hoist/kit/blueprint';
import {widgetToolbar} from '../../../../cmp/widget/WidgetToolbar';
import {widgetToolbarButton} from '../../../../cmp/widget/WidgetToolbarButton';
import {widgetToolbarSelect} from '../../../../cmp/widget/WidgetToolbarSelect';
import {RiskWindow} from '../../../../data/DataTypes';
import {MetricDef, PERFORMANCE_METRICS, RISK_METRICS} from './PerformanceMetrics';
import {PerformanceRiskWidgetModel} from './PerformanceRiskWidgetModel';
import './PerformanceComponent.scss';

const WINDOWS: RiskWindow[] = ['1Y', '3Y', 'ALL'];

/**
 * Performance and risk side by side as two grid-styled tables of the same length, and a chart of the active metric.
 * Clicking a row makes it the active metric. The tables and the chart each have their own toolbar.
 */
export const performanceComponent = hoistCmp.factory<PerformanceRiskWidgetModel>(({model}) => {
    const def = model.activeDef;
    return hframe({
        className: 'performance-widget',
        items: [
            vbox({
                className: 'performance-widget__metrics',
                items: [
                    hframe(metricTable(model, 'Performance', PERFORMANCE_METRICS), metricTable(model, `Risk · ${model.window}`, RISK_METRICS)),
                    metricsBar(model)
                ]
            }),
            vbox({
                className: 'performance-widget__chart',
                items: [
                    div({
                        className: 'performance-widget__header',
                        items: [span(def.label), span({className: 'performance-widget__subtitle', item: def.title})]
                    }),
                    chart({model: model.chartModel, flex: 1}),
                    chartBar(model)
                ]
            })
        ]
    });
});

/** Scope of the figures - the portfolio and the risk window. */
function metricsBar(model: PerformanceRiskWidgetModel) {
    return widgetToolbar({
        items: [
            widgetToolbarSelect({title: 'Portfolio', options: model.portfolioOptions, bind: 'portfolioId'}),
            toolbarSep(),
            ...WINDOWS.map(w =>
                widgetToolbarButton({
                    key: w,
                    text: w,
                    tooltip: 'Window of the risk metrics',
                    active: model.window === w,
                    onClick: () => (model.window = w)
                })
            )
        ]
    });
}

/**
 * The active chart's series as color-matched buttons, in place of a legend. Long names clip so all of them fit.
 * On most performance charts they toggle their series. On risk charts and the RMD donut they only name them.
 */
function chartBar(model: PerformanceRiskWidgetModel) {
    const {canToggleSeries} = model;
    return widgetToolbar({
        className: classNames('performance-widget__toggles', !canToggleSeries && 'performance-widget__toggles--static'),
        items: model.toggles.map(t =>
            widgetToolbarButton({
                key: t.id,
                text: t.name,
                tooltip: t.tooltip ?? t.name,
                style: {'--widget-toolbar-text-color': t.color, opacity: t.visible ? 1 : 0.4} as CSSProperties,
                onClick: canToggleSeries ? () => model.toggleSeries(t.id) : null
            })
        )
    });
}

function metricTable(model: PerformanceRiskWidgetModel, title: string, metrics: MetricDef[]) {
    const src = {figures: model.figures, risk: model.risk};
    return vbox({
        className: 'performance-widget__table',
        items: [
            div({className: 'performance-widget__header', item: title}),
            ...metrics.map((m, idx) => {
                const {text, dir} = m.value(src);
                return tooltip({
                    key: m.id,
                    compact: true,
                    position: 'right',
                    hoverOpenDelay: 400,
                    targetTagName: 'div',
                    popoverClassName: 'performance-widget__tooltip',
                    content: m.title,
                    item: div({
                        // stripes by index - each row sits in its own tooltip target, so nth-of-type can't count them
                        className: classNames(
                            'performance-widget__row',
                            idx % 2 && 'performance-widget__row--odd',
                            m.id === model.activeMetric && 'performance-widget__row--active'
                        ),
                        onClick: () => (model.activeMetric = m.id),
                        items: [
                            span({className: 'performance-widget__label', item: m.label}),
                            span({className: classNames('performance-widget__value', dir && dir !== 'flat' && `rzx-dir--${dir}`), item: text})
                        ]
                    })
                });
            })
        ]
    });
}
